import {Injectable} from '@angular/core';
import {
	AuthService,
	CacheService,
	DatepickerRepeatingService,
	DatepickerRepeatingValue,
	DateValue,
	EntityListService,
	EntityShowService,
	formatDateForServer,
	formatDateTimeForServer,
	GetAllParams,
	PaginationService,
	parseDateTimeFromServer,
	SimpleEntityService,
	stripNull
} from '@customcoding/shared';
import {HttpClient} from '@angular/common/http';
import {
	Appointment,
	AppointmentColorGroup,
	AppointmentStatus,
	CaldavAppointment,
	ConvertedAppointment
} from '../models/appointment.model';
import {firstValueFrom, Observable} from 'rxjs';
import moment, {Moment} from 'moment';
import {QueueEntry} from '../models/queue';
import {Paginated} from '@customcoding/shared/lib/models/paginated.obj';
import {PaginationParams} from '@customcoding/shared/lib/interfaces/entity-service';
import {DskAbsence} from "../models/dsk-absence";
import {MbscCalendarEvent} from "@mobiscroll/angular";
import {getAbsenceName} from "../models/absence";
import {BlockOutDatesService} from "./block-out-dates.service copy";
import {BlockOutConflict, BlockOutDate} from "../../block-out-dates/block-out-dates.model";
import {pluck} from "rxjs/operators";
import {UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {RoomConflict} from "../models/room";
import {AppointmentColorGroupService} from "./appointment-color-group.service";

@Injectable({
	providedIn: 'root'
})
export class AppointmentService implements EntityShowService<Appointment>, EntityListService<Appointment> {

	readonly apiEndpoint = '/api/scap/appointment';
	private readonly inner: SimpleEntityService<Appointment, any, any>;
	blockOutDates: BlockOutDate[] = [];
	formGroup = new UntypedFormGroup({
		id: new UntypedFormControl(), // used to keep track of editing or adding
		datepicker: new UntypedFormControl(),
		employees: new UntypedFormControl(),
		patients: new UntypedFormControl(),
		isSendEmail: new UntypedFormControl(false),
		isOnlyCaretaker: new UntypedFormControl(false),
		intervention_id: new UntypedFormControl(),
		appointment_status_id: new UntypedFormControl(),
		material_id: new UntypedFormControl(),
		location_id: new UntypedFormControl(),
		isExtern: new UntypedFormControl(false),
		isVideoConference: new UntypedFormControl(false),
		category_id: new UntypedFormControl(),
		room_id: new UntypedFormControl(),
		recurrentUUID: new UntypedFormControl(),
		description: new UntypedFormGroup({ // (mis)used for external location
			address: new UntypedFormControl(),
			general: new UntypedFormControl()
		}),
		patient_for_intervention: new UntypedFormControl(),
		noteTitle: new UntypedFormControl(),
		globalNote: new UntypedFormControl(),
		specific_notes: new UntypedFormControl(),
	});

	colorGroups: AppointmentColorGroup[] = []

	constructor(
		private http: HttpClient,
		pagination: PaginationService,
		private blockOutDatesService: BlockOutDatesService,
		private cache: CacheService,
		private auth: AuthService,
		private datepickerService: DatepickerRepeatingService,
		private appointmentColorGroupService: AppointmentColorGroupService,
	) {

		this.inner = new SimpleEntityService<Appointment, any, any>(http, pagination, this.apiEndpoint);

		//we need to wait until user is loaded:
		this.auth.getCurrentUserAsync().then(() => {
			firstValueFrom(this.appointmentColorGroupService.getAllCached()).then(colorGroups => {
				this.colorGroups = colorGroups;
			})
			this.getBlockoutDates();
		})

	}


	getBlockoutDates() {
		firstValueFrom(
			this.blockOutDatesService.getAllCached().pipe(pluck('data'))
		).then((d: BlockOutDate[]) => {
			this.blockOutDates = d.map((bd: BlockOutDate) => {
				const start = bd.isFullDay ? bd.startDate : bd.startDate + ' ' + bd.startTime;
				const end = bd.isFullDay ? bd.endDate : bd.endDate + ' ' + bd.endTime;

				return {
					...bd,
					startDate: start,
					endDate: end
				}
			});
		});
	}

	createAppointmentFromDatepicker(
		dp: DatepickerRepeatingValue,
		bodyParameters: AppointmentData,
		roomConflicts: RoomConflict[] = [],
	) {
		return this.createAppointment(
			this.datepickerService.generateRepeatingDates(dp),
			dp.repeat && dp.repeat.type != 'none',
			bodyParameters,
			roomConflicts,
		);
	}

	get(id: number): Observable<Appointment> {
		return this.inner.get(id);
	}

	all(params?: GetAllParams<AppointmentFilters, AppointmentInclude[]>): Observable<Appointment[]> {
		return this.inner.all(params);
	}

	paginated?(params: PaginationParams<AppointmentFilters, AppointmentInclude[]>): Observable<Paginated<Appointment>> {
		return this.inner.paginated(params);
	}

	/**
	 * Adds the dates selected in the datepicker to the existing appointment, forming a recurring appointment group
	 */
	addToRecurringFromDatepicker(
		appointment_id: number,
		dp: DatepickerRepeatingValue,
		bodyParameters: AppointmentData,
		rooms: RoomConflict[] = []
	) {
		return this.addToRecurringGroup(
			appointment_id,
			this.datepickerService.generateRepeatingDates(dp),
			bodyParameters,
			rooms,
		);
	}

	editAppointmentFromDatepicker(
		oldAppointment: Appointment,
		dp: DatepickerRepeatingValue,
		bodyParameters: AppointmentData,
		onlyThisAppointment: boolean,
		newRooms: any = null,
		noTimingUpdates: boolean = false,
	) {
		const oldStart = parseDateTimeFromServer(oldAppointment.startDate);
		let oldEnd = parseDateTimeFromServer(oldAppointment.endDate);
		const newStart = moment(dp.startDate).hours(dp.startTime.hours).minutes(dp.startTime.minutes).seconds(0).milliseconds(0);
		const newEnd = moment(dp.endDate).hours(dp.endTime.hours).minutes(dp.endTime.minutes).seconds(0).milliseconds(0);
		const offsetStart = newStart.diff(oldStart, 'minutes');
		const offsetEnd = newEnd.diff(oldEnd, 'minutes');

		if (oldAppointment.recurrentUUID != null && !onlyThisAppointment) {

			const {globalNote, specificNotes, ...bodyWithoutNotes} = bodyParameters;

			const data: any = {
				...bodyWithoutNotes,
				recurrentUUID: oldAppointment.recurrentUUID,
			};
			if (!noTimingUpdates) {
				data.changeStart = formatDateForServer(oldStart);
			}
			if (offsetStart != 0 && !noTimingUpdates) {
				data.offset = offsetStart;
			}
			if (offsetEnd != 0 && !noTimingUpdates) {
				data.offsetEndDate = offsetEnd;
			}

			if (noTimingUpdates) {
				delete data.startDate;
				delete data.endDate;
				delete data.datepicker;
			}

			return this.editMultiAppointment(oldAppointment.recurrentUUID, data);
		} else { //single update
			let data: any = {
				...bodyParameters
			};

			if (!noTimingUpdates) {
				data.startDate = formatDateTimeForServer(newStart);
				data.endDate = formatDateTimeForServer(newEnd)
			} else {
				delete data.startDate;
				delete data.endDate;
				delete data.datepicker;
			}
			if (newRooms[0]?.chosenRoomId) {
				data.room_id = newRooms[0]?.chosenRoomId;
			}
			return this.editAppointment(oldAppointment.id, data);
		}
	}

	/**
	 * Adds the dates to the existing appointment, forming a recurring appointment group
	 */
	addToRecurringGroup(appointment_id: number, dates: DateValue[], bodyParameters: any, rooms: RoomConflict[] = []) {
		const appointmentDates = dates.map((a) => {
			let chosenRoom: any = rooms.find(room => {
				return (room.affectedDate.startDate === a.startDate) && (room.affectedDate.endDate === a.endDate)
			})?.chosenRoomId;
			if (chosenRoom) {
				chosenRoom = chosenRoom == 'empty' ? null : chosenRoom;
			} else {
				chosenRoom = bodyParameters.room_id;
			}
			bodyParameters.isVideoConference = !!bodyParameters.isVideoConference;
			return stripNull({
				...bodyParameters,
				...a,
				room_id: chosenRoom,
			});
		});

		return this.http.post(`${this.apiEndpoint}/recurrent/${appointment_id}`, {
			...stripNull(bodyParameters),
			appointments: appointmentDates
		});
	}

	createAppointment(
		dates: DateValue[],
		isRecurrent: boolean,
		bodyParameters: any,
		rooms: RoomConflict[] = []
	): Observable<Appointment[]> {
		const appointmentDates = dates.map((a) => {
			let chosenRoom: any = rooms.find(room => {
				return (room.affectedDate.startDate === a.startDate) && (room.affectedDate.endDate === a.endDate)
			})?.chosenRoomId;
			if (chosenRoom) {
				chosenRoom = chosenRoom == 'empty' ? null : chosenRoom;
			} else {
				chosenRoom = bodyParameters.room_id;
			}
			bodyParameters.isVideoConference = !!bodyParameters.isVideoConference;
			return stripNull({
				...bodyParameters,
				...a,
				room_id: chosenRoom,
			});
		});

		return this.http.post<Appointment[]>(this.apiEndpoint, {
			...stripNull(bodyParameters),
			isRecurrent,
			appointments: appointmentDates
		});
	}

	createAppointmentsWithRoomArray(dp: DatepickerRepeatingValue, rooms: RoomConflict[], bodyParameters: any, appointment_id: number = null): Observable<Appointment[]> {
		const dates = this.datepickerService.generateRepeatingDates(dp);
		let appointments: any[] = [];
		dates.forEach((date) => {
			let chosenRoom: any = rooms.find(room => {
				return (room.affectedDate.startDate === date.startDate) && (room.affectedDate.endDate === date.endDate)
			})?.chosenRoomId;
			if (chosenRoom) {
				chosenRoom = chosenRoom == 'empty' ? null : chosenRoom;
			} else {
				chosenRoom = bodyParameters.room_id;
			}
			bodyParameters.isVideoConference = !!bodyParameters.isVideoConference; // null causes problems -> cast to boolean
			appointments.push({
				...date,
				...bodyParameters,
				room_id: chosenRoom,
			})
		})
		return this.http.post<Appointment[]>(this.apiEndpoint, {
			isRecurrent: true,
			appointments: appointments,
			appointment_id,
			employees: bodyParameters.employees,
			intervention_id: bodyParameters.intervention_id,
			patient_for_intervention: bodyParameters.patient_for_intervention,
			patients: bodyParameters.patients,
		});
	}

	getFilteredAppointments(params: GetAllParams<AppointmentFilters, AppointmentInclude[]>): Observable<Appointment[]> {
		const filters = params.filters;
		if (!filters['employees.id']) {
			delete filters['employees.id'];
		}
		// the following restores the old functionality of the startDate and endDate filter
		// @Elia Added last condition to make it working also with one date only
		if (filters['startDate'] && !filters['startDate'].startsWith('+') && !filters['startDate'].startsWith('-')) {
			filters.startDate = '+' + filters['startDate'];
		}
		if (filters['endDate'] && !filters['endDate'].startsWith('-') && !filters['endDate'].startsWith('+')) {
			filters.endDate = '-' + filters['endDate'];
		}
		return this.all({
			...params,
			include: params.include || AllAppointmentIncludes as any as Mutable<AppointmentInclude[]>,
			filters
		});
	}

	editMultiAppointment(recurrentUUID: string, bodyParameters: MultiAppointmentUpdateData) {
		let data: any = {...stripNull(bodyParameters)};

		return this.http.patch(this.apiEndpoint, data);
	}

	addPatient(appointment_id: number, patient_intervention_id: QueueEntry['pivot']['id'], startDate: Moment) {
		return this.http.post(`/api/scap/appointments/${appointment_id}/intervention-patient/${patient_intervention_id}`, {
			startDate: formatDateForServer(startDate)
		});
	}

	editAppointment(id: number, bodyParameters: AppointmentData & Partial<DateValue>): Observable<Appointment> {
		return this.http.patch<Appointment>(`${this.apiEndpoint}/${id}`, stripNull(bodyParameters));
	}

	deleteAppointment(id: number) {
		return this.http.delete(`${this.apiEndpoint}/${id}`);
	}

	getAllStatus(): Observable<AppointmentStatus[]> {
		return this.cache.get('/api/scap/appointment-status');
	}

	updatePatientStatus(appointmentStatusId: number, appointmentId: number, patientId: number) {
		return this.http.patch(`/api/scap/appointments/${appointmentId}/patient/${patientId}`, {appointment_status_id: appointmentStatusId});
	}


	/* CALENDAR HELPER */

	convertAppointment(a: Appointment): ConvertedAppointment {

		let matchedItem = null;

		if (a.appointmentable_type == 'Modules\\Scap\\Entities\\AppointmentCategory') {
			matchedItem = this.colorGroups.find(cg => {
				if (!cg.appointment_categories?.length) return false;
				return !!cg.appointment_categories.find(ac => {
					return ac.id == a.appointmentable_id
				})
			})
		} else {
			matchedItem = this.colorGroups.find(cg => {
				if (!cg.interventions?.length) return false;
				return !!cg.interventions.find(intervention => {
					return intervention.id == a.appointmentable_id
				})
			})
		}

		let appointmentEmployees = a.employees.map(e => e.id);
		return {
			id: a.id as any,
			start: a.startDate,
			end: a.endDate,
			title: a.title,
			color: matchedItem?.color,
			appointment: a,
			resource: appointmentEmployees
		};

	}


	convertCalDavAppointment(a: CaldavAppointment): ConvertedAppointment {
		return {
			start: a.startDate,
			end: a.endDate,
			title: a.title,
			calDav: a
		};
	}

	convertAbsence(a: DskAbsence): MbscCalendarEvent {

		const start = a.startDate.split(' ');
		const end = a.endDate.split(' ');
		if (start.length < 2 || end.length < 2) {
			return {};
		}
		const isAllDay = a.startDate == a.endDate || (start[0] == end[0] && start[1] == '00:00:00' && end[1] == '23:59:59');
		return {

			id: null,
			start: a.startDate,
			end: a.endDate,
			allDay: isAllDay,
			resource: a.employee_id,
			title: getAbsenceName(a.type),
			//TODO:display: isBackground ? 'background' : 'auto',
			background: 'rgb(241,241,241)',
			extendedProps: {
				type: CalendarEntryTypes.absence
			}
			/*
            id: '',
            start: a.startDate,
            end: a.endDate,
            allDay: isAllDay,
            title: a.title,
            display: isAllDay ? 'background' : 'auto',

            backgroundColor: employee ? this.employeeColors[employee] : '#d7d7d7',
            extendedProps: {
                employee_id: a.user_id,
                type: CalendarEntryTypes.absence
            }
            */
			// textColor: a.appointment_status_id == 1 ? "#FC4C87" : null //is overwritten by metronic
		};
	}

	getPatientAppointmentStatus(appointment: Appointment, patientId: number) {

		const patient = appointment.patients.find(patient => patient.id == patientId);
		return patient.pivot.appointment_status_id != null ? patient.pivot.appointment_status_id : appointment.appointment_status_id;
	}


	/* BLOCK OUT COLLISSION CHECK */

	checkAgainstBlockOutDates(dates: any) {
		return this.findCollisions(dates, this.blockOutDates);

	}

	findCollisions(dates1: any[] | any, dates2: any[]): any[] {
		const conflicts: BlockOutConflict[] = [];
		if (Array.isArray(dates1)) {
			dates1.forEach(date1 => {
				dates2.forEach(date2 => {
					if (this.checkCollision(date1.startDate, date1.endDate, date2.startDate, date2.endDate)) {
						conflicts.push({date: date1, blockOutDate: date2, resolved: false});
					}
				});
			});
		} else {
			dates2.forEach(date2 => {
				if (this.checkCollision(dates1.startDate, dates1.endDate, date2.startDate, date2.endDate)) {
					conflicts.push({date: dates1, blockOutDate: date2, resolved: false});
				}
			});
		}

		return conflicts;
	}


	checkCollision(startDateTime1: string, endDateTime1: string, startDateTime2: string, endDateTime2: string): boolean {
		const date1Start = new Date(startDateTime1);
		const date1End = new Date(endDateTime1);
		const date2Start = new Date(startDateTime2);
		const date2End = new Date(endDateTime2);

		// Assuming overlapping if start of one date falls between start and end of the other date
		return (date1Start <= date2End && date1Start >= date2Start) ||
			(date2Start <= date1End && date2Start >= date1Start);
	}

	declineAppointmentRequest(appointmentId: number) {
		return this.http.post(`/api/scap/appointment/${appointmentId}/employee/decline`, {_method: 'PATCH'})
	}

	acceptAppointmentRequest(appointmentId: number) {
		return this.http.post(`/api/scap/appointment/${appointmentId}/employee/confirm`, {_method: 'PATCH'})
	}

	confirmPlannedAppointment(appointmentId: number, statusId: number) {
		return this.inner.update(appointmentId, {
			appointment_status_id: statusId,
		})
	}
}

export type AppointmentFilters = Partial<{
	'employees.id': number | string;
	'patients.id': number | string;
	'appointmentable_type': string; //appointmentableType
	'appointmentable_id': number | string;
	'status_id': number | string;
	'user_id': number | string;
	'location_id': number | string;
	'startDate': string;
	'endDate': string;
}>;


const AllAppointmentIncludes = <const>
	['patients', 'patients.person', 'employees.user.person', 'location.country', 'appointmentable',
		'patients.person.avatar', 'employees.person.avatar', 'appointmentStatus', 'patients.custodyCaretaker.person'];


type AppointmentInclude = (typeof AllAppointmentIncludes)[number];

type Mutable<T> = {
	-readonly [P in keyof T]: T[P];
};

export type AppointmentData = Partial<{
	appointment_status_id: number;
	title: string;
	isHomeOffice: boolean;
	intervention_id: number;
	assessment_id: number;
	patients: number[];
	employees: number[];
	material_id: number[];
	location_id: number;
	room_id: number;
	category_id: number;
	description: any;
	globalNote: string;
	specificNotes: { patientId: number; text: string }[];
}>;

export type MultiAppointmentUpdateData = AppointmentData & Partial<{
	recurrentUUID: string;
	changeStart: string; //Date
	changeEnd: string; //Date
	offset: number; //Minutes
	offsetEndDate: number; //Minutes
}>

enum CalendarEntryTypes {
	caldav = 'caldav',
	workTime = 'worktime',
	absence = 'absence'
}
